import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import customerService from '../services/customerService';
import { useAuth } from '../contexts/AuthContext';


const CustomerAddLayer = () => {
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    
    // Form state
    const [formData, setFormData] = useState({
        company_name: '',
        company_email: '',
        company_phone: '',
        industry: '',
        website: '',
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        job_title: '',
        street_address: '',
        city: '',
        state_province: '',
        postal_code: '',
        country: '',
        notes: '',
        status: 'active',
        company_logo: null
    });
    
    const [logoPreviewUrl, setLogoPreviewUrl] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoadingCountries, setIsLoadingCountries] = useState(true);
    const [countries, setCountries] = useState([]);
    const [filteredCountries, setFilteredCountries] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);
    const [errors, setErrors] = useState({});
    const [emailStatus, setEmailStatus] = useState({ valid: true, message: '' });
    const emailCheckTimeout = useRef(null);


    // Load countries on component mount
    useEffect(() => {
        const loadCountries = async () => {
            try {
                const countriesList = await customerService.getCountries();
                setCountries(countriesList);
                setFilteredCountries(countriesList);
            } catch (error) {
                console.error('Failed to load countries:', error);
                
                // Handle network errors (backend down)
                if (error.code === 'ERR_NETWORK' || !error.response) {
                    toast.error('Unable to load country list. Please check if the backend service is running.', {
                        position: 'top-right',
                        autoClose: 10000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                    });
                } 
                // Handle server errors (5xx)
                else if (error.response?.status >= 500) {
                    toast.error('Server error while loading countries. Please try again later.', {
                        position: 'top-right',
                        autoClose: 10000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                    });
                }
                // For other errors, use the default message
                else {
                    toast.error('Failed to load country list. Using default list.', {
                        position: 'top-right',
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                    });
                }
            } finally {
                setIsLoadingCountries(false);
            }
        };

        loadCountries();
    }, []);

    // Filter countries based on search term
    useEffect(() => {
        if (!searchTerm.trim()) {
            setFilteredCountries(countries);
        } else {
            const filtered = countries.filter(country =>
                country.name.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredCountries(filtered);
        }
    }, [searchTerm, countries]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const toggleDropdown = () => {
        setIsDropdownOpen(!isDropdownOpen);
        if (!isDropdownOpen) {
            setSearchTerm('');
        }
    };

    const handleCountrySelect = (country) => {
        handleChange({
            target: {
                name: 'country',
                value: country.name
            }
        });
        setIsDropdownOpen(false);
        setSearchTerm('');
    };

    // Function to check if email exists with debounce and better error handling
    const checkEmailExists = async (email) => {
        // Reset status immediately for better UX
        setEmailStatus({ valid: true, message: '', loading: true });
        
        // Basic validation
        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setEmailStatus({ valid: true, message: '', loading: false });
            return;
        }

        try {
            const exists = await customerService.checkEmailExists(email);
            if (exists) {
                setEmailStatus({
                    valid: false,
                    message: 'A customer with this email already exists.',
                    loading: false
                });
            } else {
                setEmailStatus({ valid: true, message: '', loading: false });
            }
        } catch (error) {
            console.error('Error checking email:', error);
            // Show error only if it's not a 404 (email not found is a valid case)
            const isNotFoundError = error.response?.status === 404;
            
            setEmailStatus({
                valid: isNotFoundError ? true : false,
                message: isNotFoundError ? '' : 'Error checking email availability',
                loading: false
            });
        }
    };

    // Handle input changes
    const handleChange = (e) => {
        const { name, value, files } = e.target;
        
        // Update form data
        if (name === 'company_logo' && files && files[0]) {
            const file = files[0];
            console.log('file', file);
            // Validate image type and size (max 2MB) – mirrors CustomerProfileLayer logic
            if (!file.type.startsWith('image/')) {
                toast.error('Please upload an image file');
                return;
            }
            if (file.size > 2 * 1024 * 1024) {
                toast.error('Image size should be less than 2MB');
                return;
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoPreviewUrl(reader.result);
            };
            reader.onerror = () => {
                toast.error('Failed to load image');
            };
            reader.readAsDataURL(file);

            setFormData(prev => ({
                ...prev,
                [name]: file
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));

            // Check email field specifically
            if (name === 'company_email') {
                clearTimeout(emailCheckTimeout.current);
                emailCheckTimeout.current = setTimeout(() => {
                    checkEmailExists(value);
                }, 500);
            }
        }
        
        // Clear error for this field if it exists
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
    };
    
    // Validate form fields
    const validateForm = () => {
        const newErrors = {};
        
        // Required fields validation
        if (!formData.company_name?.trim()) {
            newErrors.company_name = 'Company name is required';
        }
        
        if (!formData.company_email?.trim()) {
            newErrors.company_email = 'Company email is required';
        } else if (!/^\S+@\S+\.\S+$/.test(formData.company_email)) {
            newErrors.company_email = 'Please enter a valid email address';
        }
        
        if (!formData.contact_name?.trim()) {
            newErrors.contact_name = 'Contact name is required';
        }
        
        // Phone number validation (if provided)
        if (formData.company_phone && !/^[\d\s\-+()]*$/.test(formData.company_phone)) {
            newErrors.company_phone = 'Please enter a valid phone number';
        }
        
        // Contact email validation (if provided)
        if (formData.contact_email && !/^\S+@\S+\.\S+$/.test(formData.contact_email)) {
            newErrors.contact_email = 'Please enter a valid email address';
        }
        
        // Contact phone validation (if provided)
        if (formData.contact_phone && !/^[\d\s\-+()]*$/.test(formData.contact_phone)) {
            newErrors.contact_phone = 'Please enter a valid phone number';
        }
        
        // Address validations (if any address field is provided)
        const addressFields = [formData.street_address, formData.city, formData.state_province, formData.postal_code, formData.country];
        const hasAddressInfo = addressFields.some(field => field?.trim());
        
        if (hasAddressInfo) {
            if (!formData.street_address?.trim()) {
                newErrors.street_address = 'Street address is required when providing address information';
            }
            if (!formData.city?.trim()) {
                newErrors.city = 'City is required when providing address information';
            }
            if (!formData.country?.trim()) {
                newErrors.country = 'Country is required when providing address information';
            }
        }
        
        return newErrors;
    };
    
    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setErrors({});
        
        // Basic validation
        if (!formData.company_name || !formData.company_email || !formData.contact_name) {
            toast.error('Please fill in all required fields', {
                position: 'top-right',
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });
            return;
        }

        // Check if email is already in use
        if (!emailStatus.valid) {
            toast.error(emailStatus.message, {
                position: 'top-right',
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });
            return;
        }

        // Validate form
        const formErrors = validateForm();
        if (Object.keys(formErrors).length > 0) {
            setErrors(formErrors);
            
            // Show toast for each validation error
            Object.entries(formErrors).forEach(([field, errorMessage]) => {
                toast.error(`${field.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}: ${errorMessage}`, {
                    position: 'top-right',
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                });
            });
            
            // Scroll to first error field
            const firstErrorField = Object.keys(formErrors)[0];
            const element = document.getElementById(firstErrorField) || 
                           document.querySelector(`[name="${firstErrorField}"]`);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                element.focus({ preventScroll: true });
            }
            return;
        }
        
        setIsSubmitting(true);
        setErrors({});
        
        try {
            const formDataToSend = new FormData();
            
            // Add required fields first
            formDataToSend.append('company_name', formData.company_name || '');
            formDataToSend.append('company_email', formData.company_email || '');
            formDataToSend.append('contact_name', formData.contact_name || '');
            
            // Add optional fields if they have values
            const optionalFields = [
                'company_phone', 'industry', 'website', 'contact_email', 
                'contact_phone', 'job_title', 'street_address', 'city', 
                'state_province', 'postal_code', 'country', 'notes'
            ];
            
            optionalFields.forEach(field => {
                if (formData[field] !== undefined && formData[field] !== null && formData[field] !== '') {
                    formDataToSend.append(field, formData[field]);
                }
            });
            
            // Handle file upload with better logging
            if (formData.company_logo instanceof File) {
                console.log('[CustomerAdd] Adding logo to form data:', {
                    name: formData.company_logo.name,
                    type: formData.company_logo.type,
                    size: formData.company_logo.size,
                    lastModified: formData.company_logo.lastModified
                });
                formDataToSend.append('logo', formData.company_logo, formData.company_logo.name);
                
                // Log FormData entries for debugging
                for (let pair of formDataToSend.entries()) {
                    console.log('[CustomerAdd] FormData entry:', pair[0], pair[1]);
                }
            } else {
                console.log('[CustomerAdd] No valid logo file to upload');
            }
            
            // Add status with default value if not set
            formDataToSend.append('status', formData.status || 'active');
            
            // Add owner_id from current user
            if (currentUser?.id) {
                formDataToSend.append('owner_id', currentUser.id);
            }
            
            console.info('formDataToSend===>', formDataToSend);
            await customerService.createCustomer(formDataToSend);
            toast.success('Customer created successfully!');
            navigate('/admin/customers');
        } catch (error) {
            console.error('Error creating customer:', error);
            
            // Check if this is a session expiration error
            if (error.message === 'Your session has expired. Please log in again.') {
                // Don't show an additional toast as the user will be redirected
                return;
            }
            
            // Handle validation errors from the server
            if (error.response?.data?.detail) {
                // Handle Pydantic validation errors
                const errorDetails = error.response.data.detail;
                const validationErrors = {};
                
                errorDetails.forEach(detail => {
                    const field = detail.loc[detail.loc.length - 1];
                    validationErrors[field] = detail.msg;
                    
                    // Show toast for each validation error
                    toast.error(`${field.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}: ${detail.msg}`, {
                        position: 'top-right',
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                    });
                });
                
                setErrors(validationErrors);
                
                // Scroll to the first error field
                const firstErrorField = Object.keys(validationErrors)[0];
                const element = document.getElementById(firstErrorField) || 
                               document.querySelector(`[name="${firstErrorField}"]`);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    element.focus({ preventScroll: true });
                }
            } else if (error.validation) {
                // Handle client-side validation errors
                setErrors(error.validation);
                
                // Show toast for each validation error
                Object.entries(error.validation).forEach(([field, errorMessage]) => {
                    toast.error(`${field.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}: ${errorMessage}`, {
                        position: 'top-right',
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                    });
                });
                
                // Scroll to the first error field
                const firstErrorField = Object.keys(error.validation)[0];
                const element = document.getElementById(firstErrorField) || 
                               document.querySelector(`[name="${firstErrorField}"]`);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    element.focus({ preventScroll: true });
                }
            } else {
                // Handle other errors
                const errorMessage = error.message || 'Failed to create customer. Please try again.';
                toast.error(errorMessage);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-body p-24">
                <div className="row justify-content-center">
                    <div className="col-xxl-10 col-xl-10 col-lg-12">
                        <div className="card border">
                            <div className="card-header bg-base py-16 px-24">
                                <h5 className="card-title text-lg fw-semibold text-primary-light mb-0">Customer Information</h5>
                            </div>
                            <div className="card-body p-24">
                                <form onSubmit={handleSubmit}>
                                    <div className="row mb-24">
                                        <div className="col-md-6">
                                            {/* Company Logo Upload */}
                                            <div className="mb-24">
                                                <h6 className="text-md text-primary-light mb-16">Company Logo</h6>
                                                <div className="avatar-upload">
                                                    <div className="avatar-edit position-absolute bottom-0 end-0 me-24 mt-16 z-1 cursor-pointer">
                                                        <input
                                                            type="file"
                                                            id="logoUpload"
                                                            accept=".png, .jpg, .jpeg"
                                                            hidden
                                                            name="company_logo"
                                                            onChange={handleChange}
                                                        />
                                                        <label
                                                            htmlFor="logoUpload"
                                                            className="w-32-px h-32-px d-flex justify-content-center align-items-center bg-primary-50 text-primary-600 border border-primary-600 bg-hover-primary-100 text-lg rounded-circle">
                                                            <Icon icon="solar:camera-outline" className="icon"></Icon>
                                                        </label>
                                                    </div>
                                                    <div className="avatar-preview">
                                                        {logoPreviewUrl ? (
                                                            // <img src={logoPreviewUrl} 
                                                            //     alt="Company Logo Preview" 
                                                            //     className="rounded-circle" 
                                                            //     style={{ width: '100px', height: '100px', objectFit: 'cover' }} 
                                                            //     />
                                                            <div
                                                                id="logoPreview"
                                                                style={{
                                                                    backgroundImage: logoPreviewUrl ? `url(${logoPreviewUrl})` : '',
                                                                }}
                                                            >
                                                            </div>
                                                        ) : (
                                                            <div className="d-flex flex-column align-items-center justify-content-center w-100 h-100 bg-light rounded-circle">
                                                                <Icon icon="solar:gallery-add-bold" className="text-muted mb-2" width={24} />
                                                                <span className="text-xs text-muted">Upload Logo</span>
                                                            </div>
                                                        )}
                                                        {errors.company_logo && (
                                                            <div className="text-danger small mt-2">{errors.company_logo}</div>
                                                        )}
                                                        
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="row gy-4">
                                        {/* Company Information */}
                                        <div className="col-12">
                                            <h6 className="text-md text-primary-light mb-16">Company Details</h6>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="mb-20">
                                                <h6 className="text-md text-primary-light mb-16">Company Name <span className="text-danger">*</span></h6>
                                                <input 
                                                    type="text" 
                                                    name="company_name"
                                                    value={formData.company_name}
                                                    onChange={handleChange}
                                                    className={`form-control ${errors.company_name ? 'is-invalid' : ''}`} 
                                                    placeholder="Enter company name" 
                                                />
                                                {errors.company_name && (
                                                    <div className="invalid-feedback">{errors.company_name}</div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="mb-20">
                                                <label
                                                    htmlFor="industry"
                                                    className="form-label fw-semibold text-primary-light text-sm mb-8"
                                                >
                                                    Industry
                                                </label>
                                                <select
                                                    className="form-control radius-8 form-select"
                                                    id="industry"
                                                    name="industry"
                                                    value={formData.industry}
                                                    onChange={handleChange}
                                                    defaultValue="Select Industry"
                                                >
                                                    <option value="Select Industry" disabled>
                                                        Select Industry
                                                    </option>
                                                    <option value="Technology">Technology</option>
                                                    <option value="Manufacturing">Manufacturing</option>
                                                    <option value="Healthcare">Healthcare</option>
                                                    <option value="Finance">Finance</option>
                                                    <option value="Education">Education</option>
                                                    <option value="pending">Pending</option>
                                                    <option value="Other">Other</option>
                                                </select>
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="mb-20">
                                                <label
                                                    htmlFor="companyEmail"
                                                    className="form-label fw-semibold text-primary-light text-sm mb-8"
                                                >
                                                    Company Email <span className="text-danger-600">*</span>
                                                </label>
                                                <div className="relative">
                                                    <input
                                                        type="email"
                                                        id="company_email"
                                                        name="company_email"
                                                        value={formData.company_email}
                                                        onChange={handleChange}
                                                        className={`form-control radius-8 ${!emailStatus.valid ? 'is-invalid' : ''}`}
                                                        required
                                                    />
                                                    
                                                </div>
                                                {!emailStatus.valid && (
                                                    <p className="mt-1 text-sm text-danger">{emailStatus.message}</p>
                                                )}
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="mb-20">
                                                <label
                                                    htmlFor="companyPhone"
                                                    className="form-label fw-semibold text-primary-light text-sm mb-8"
                                                >
                                                    Company Phone <span className="text-danger-600">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    name="company_phone"
                                                    value={formData.company_phone}
                                                    onChange={handleChange}
                                                    className="form-control radius-8"
                                                    id="companyPhone"
                                                    placeholder="+1 (555) 000-0000"
                                                />
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="mb-20">
                                                <label
                                                    htmlFor="website"
                                                    className="form-label fw-semibold text-primary-light text-sm mb-8"
                                                >
                                                    Website
                                                </label>
                                                <input
                                                    type="text"
                                                    name="website"
                                                    value={formData.website}
                                                    onChange={handleChange}
                                                    className="form-control radius-8"
                                                    id="website"
                                                    placeholder="Enter Website URL"
                                                />
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="mb-20">
                                                <label
                                                    htmlFor="status"
                                                    className="form-label fw-semibold text-primary-light text-sm mb-8"
                                                >
                                                    Status <span className="text-danger-600">*</span>
                                                </label>
                                                <select
                                                    className={`form-control radius-8 form-select ${errors.status ? 'is-invalid' : ''}`}
                                                    id="status"
                                                    name="status"
                                                    value={formData.status || ''}
                                                    onChange={handleChange}
                                                    required
                                                >
                                                    <option value="" disabled>
                                                        Select Status
                                                    </option>
                                                    <option value="active">Active</option>
                                                    <option value="inactive">Inactive</option>
                                                </select>
                                                {errors.status && (
                                                    <div className="invalid-feedback">{errors.status}</div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Primary Contact Information */}
                                        <div className="col-12 mt-4">
                                            <h6 className="text-md text-primary-light mb-16">Primary Contact</h6>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="mb-20">
                                                <label
                                                    htmlFor="contactName"
                                                    className="form-label fw-semibold text-primary-light text-sm mb-8"
                                                >
                                                    Contact Name <span className="text-danger-600">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    name="contact_name"
                                                    value={formData.contact_name}
                                                    onChange={handleChange}
                                                    className={`form-control radius-8 ${errors.contact_name ? 'is-invalid' : ''}`}
                                                    id="contactName"
                                                    placeholder="Enter Contact Name"
                                                />
                                                {errors.contact_name && (
                                                    <div className="invalid-feedback">{errors.contact_name}</div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="mb-20">
                                                <label
                                                    htmlFor="contactTitle"
                                                    className="form-label fw-semibold text-primary-light text-sm mb-8"
                                                >
                                                    Job Title
                                                </label>
                                                <input
                                                    type="text"
                                                    name="job_title"
                                                    value={formData.job_title}
                                                    onChange={handleChange}
                                                    className="form-control radius-8"
                                                    id="contactTitle"
                                                    placeholder="Enter Job Title"
                                                />
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="mb-20">
                                                <label
                                                    htmlFor="contactEmail"
                                                    className="form-label fw-semibold text-primary-light text-sm mb-8"
                                                >
                                                    Email <span className="text-danger-600">*</span>
                                                </label>
                                                <input
                                                    type="email"
                                                    name="contact_email"
                                                    value={formData.contact_email}
                                                    onChange={handleChange}
                                                    className="form-control radius-8"
                                                    id="contactEmail"
                                                    placeholder="Enter Email"
                                                />
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="mb-20">
                                                <label
                                                    htmlFor="contactPhone"
                                                    className="form-label fw-semibold text-primary-light text-sm mb-8"
                                                >
                                                    Phone <span className="text-danger-600">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    name="contact_phone"
                                                    value={formData.contact_phone}
                                                    onChange={handleChange}
                                                    className="form-control radius-8"
                                                    id="contactPhone"
                                                    placeholder="+1 (555) 000-0000"
                                                />
                                            </div>
                                        </div>

                                        {/* Address Information */}
                                        <div className="col-12 mt-4">
                                            <h6 className="text-md text-primary-light mb-16">Address Information</h6>
                                        </div>

                                        <div className="col-md-12">
                                            <div className="mb-20">
                                                <label
                                                    htmlFor="address"
                                                    className="form-label fw-semibold text-primary-light text-sm mb-8"
                                                >
                                                    Street Address <span className="text-danger-600">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    name="street_address"
                                                    value={formData.street_address}
                                                    onChange={handleChange}
                                                    className="form-control radius-8"
                                                    id="address"
                                                    placeholder="Enter Street Address"
                                                />
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="mb-20">
                                                <label
                                                    htmlFor="city"
                                                    className="form-label fw-semibold text-primary-light text-sm mb-8"
                                                >
                                                    City <span className="text-danger-600">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    name="city"
                                                    value={formData.city}
                                                    onChange={handleChange}
                                                    className="form-control radius-8"
                                                    id="city"
                                                    placeholder="Enter City"
                                                />
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="mb-20">
                                                <label
                                                    htmlFor="state"
                                                    className="form-label fw-semibold text-primary-light text-sm mb-8"
                                                >
                                                    State/Province <span className="text-danger-600">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    name="state_province"
                                                    value={formData.state_province}
                                                    onChange={handleChange}
                                                    className="form-control radius-8"
                                                    id="state"
                                                    placeholder="Enter State/Province"
                                                />
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="mb-20">
                                                <label
                                                    htmlFor="postalCode"
                                                    className="form-label fw-semibold text-primary-light text-sm mb-8"
                                                >
                                                    Postal Code <span className="text-danger-600">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    name="postal_code"
                                                    value={formData.postal_code}
                                                    onChange={handleChange}
                                                    className="form-control radius-8"
                                                    id="postalCode"
                                                    placeholder="Enter Postal Code"
                                                />
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="mb-20">
                                                <label
                                                    htmlFor="country"
                                                    className="form-label fw-semibold text-primary-light text-sm mb-8"
                                                >
                                                    Country <span className="text-danger-600">*</span>
                                                </label>
                                                {isLoadingCountries ? (
                                                    <div className="d-flex align-items-center">
                                                        <div className="spinner-border spinner-border-sm me-2" role="status">
                                                            <span className="visually-hidden">Loading...</span>
                                                        </div>
                                                        <span>Loading countries...</span>
                                                    </div>
                                                ) : (
                                                    <div className="position-relative" ref={dropdownRef}>
                                                        <div 
                                                            className={`form-control radius-8 d-flex align-items-center justify-content-between ${errors.country ? 'is-invalid' : ''} ${isDropdownOpen ? 'border-primary' : ''}`}
                                                            style={{ cursor: 'pointer', minHeight: '38px' }}
                                                            onClick={toggleDropdown}
                                                        >
                                                            <span className={`${!formData.country ? 'text-muted' : ''}`}>
                                                                {formData.country || 'Select Country'}
                                                            </span>
                                                            <Icon 
                                                                icon={isDropdownOpen ? 'mdi:chevron-up' : 'mdi:chevron-down'} 
                                                                className="text-muted"
                                                            />
                                                        </div>
                                                        
                                                        {isDropdownOpen && (
                                                            <div className="position-absolute w-100 bg-white shadow-lg rounded mt-1 z-10" style={{ maxHeight: '300px', overflow: 'hidden' }}>
                                                                <div className="p-2 border-bottom">
                                                                    <div className="input-group input-group-sm">
                                                                        <span className="input-group-text bg-white">
                                                                            <Icon icon="mdi:magnify" />
                                                                        </span>
                                                                        <input
                                                                            type="text"
                                                                            className="form-control form-control-sm border-start-0"
                                                                            placeholder="Search countries..."
                                                                            value={searchTerm}
                                                                            onChange={(e) => {
                                                                                e.stopPropagation();
                                                                                setSearchTerm(e.target.value);
                                                                            }}
                                                                            onClick={(e) => e.stopPropagation()}
                                                                            autoFocus
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                                                                    {filteredCountries.length > 0 ? (
                                                                        filteredCountries.map((country) => (
                                                                            <div 
                                                                                key={country.code}
                                                                                className={`px-3 py-2 dropdown-item cursor-pointer ${formData.country === country.name ? 'bg-light' : ''}`}
                                                                                onClick={() => handleCountrySelect(country)}
                                                                            >
                                                                                {country.name}
                                                                            </div>
                                                                        ))
                                                                    ) : (
                                                                        <div className="dropdown-item text-muted">No countries found</div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                                {errors.country && (
                                                    <div className="invalid-feedback">{errors.country}</div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Notes */}
                                        <div className="col-12 mt-4">
                                            <h6 className="text-md text-primary-light mb-16">Additional Information</h6>
                                        </div>

                                        <div className="col-md-12">
                                            <div className="mb-20">
                                                <label
                                                    htmlFor="notes"
                                                    className="form-label fw-semibold text-primary-light text-sm mb-8"
                                                >
                                                    Notes
                                                </label>
                                                <textarea
                                                    className="form-control radius-8"
                                                    id="notes"
                                                    name="notes"
                                                    value={formData.notes}
                                                    onChange={handleChange}
                                                    rows="4"
                                                    placeholder="Enter any additional notes about this customer..."
                                                ></textarea>
                                            </div>
                                        </div>

                                        <div className="col-12 mt-4">
                                            <div className="d-flex align-items-center justify-content-end gap-3">
                                                <button
                                                    type="button"
                                                    onClick={() => navigate('/admin/customers')}
                                                    className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-56 py-11 radius-8"
                                                    disabled={isSubmitting}
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    type="submit"
                                                    className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8"
                                                    disabled={isSubmitting}
                                                >
                                                    {isSubmitting ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                            Saving...
                                                        </>
                                                    ) : 'Save Customer'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CustomerAddLayer;
